/* eslint-disable */

// import { browser, Storage } from "webextension-polyfill-ts";
import { TreeRange } from "./Range";
import { ClientFeedbackRequest } from "./ClientFeedbackRequest";
import GrammarClient, { AdviceLanguage } from "./GrammarClient";
import { Feedback, GrammarScore } from "./GrammarScore";
import { Response } from "./Response";
import htmlRangeClient from "./HtmlRangeApi";

interface ExtensionMessage<T> {
  type: string;
  payload: T;
}

const FEEDBACK_LANGUAGE = "ENGLISH";

// const BASE_URL = 'http://localhost:8080';
const BASE_URL =
  process.env.WRITESHIFT_PROXY || "https://api.correctenglish-staging.com";
if (!BASE_URL) {
  throw new Error(`Cannot find base url for writeshift proxy: ${BASE_URL}`);
}
const grammarClient: GrammarClient = new GrammarClient(
  {
    appId: "regression testing",
    authorId: "tmagnusson@vantage.com",
  },
  {
    adviceDetailLevel: "THREE",
    adviceLanguage: "ENGLISH",
    checkLevel: 3,
    checkMode: "FULL_CHECK",
    dialect: "American",
    dialectOption: "ize",
    formalityLevel: "Formal",
    ignoreNonEnglishInput: false,
    ignoreSeverity3: false,
    language: "American English",
    productName: "ALL",
    ruleSettingMethod: "LEVELS",
    ruleTypeMap: {},
    spellerChoice: "BASIC",
  },
  {
    baseUrl: BASE_URL,
    apiKey: "nothing",
    clientId: "nope",
  }
);

const WRITESHIFT_CHECK = "WRITESHIFT/CHECK";
export type WriteShiftRequest = {
  text: string;
  docId: string;
  language: AdviceLanguage;
};

// Listen for changes in language, and update the grammar client as necessary
// function languageListener(changes: { [key: string]: Storage.StorageChange }) {
//   Object.entries(changes)
//     .filter(([k]) => k === FEEDBACK_LANGUAGE)
//     .forEach(([, { newValue }]) => {
//       if (newValue !== undefined) {
//         grammarClient.setLanguage(newValue);
//       }
//     });
// }

// Update the language initially
// browser.storage.onChanged.addListener(languageListener);
// (async () => {
//   // Type safety: this is enforced through useFeedbackLanguage()
//   const currentLanguage: AdviceLanguage | undefined = (
//     await browser.storage.local.get([FEEDBACK_LANGUAGE])
//   )[FEEDBACK_LANGUAGE];
//   if (currentLanguage) {
//     grammarClient.setLanguage(currentLanguage);
//   }
// })();

export function supportsWriteShiftMessage(
  message: ExtensionMessage<unknown>
): boolean {
  return message.type === WRITESHIFT_CHECK;
}

/**
 * I wish TypeScript would support spreading for types
 *
 * type ResponseWithHtmlRanges = {
 *      ...ResponseWithHtmlRanges,
 *      score: {
 *          ...ResponseWithHtmlRanges.Score
 *          feedbackList: FeedbackWithRange[]
 *      }
 * }
 */
export type FeedbackWithRange = Feedback & {
  highlightRange: TreeRange;
  gated: boolean;
};
type ScoreWithRanges = Omit<GrammarScore, "feedbackList"> & {
  feedbackList: FeedbackWithRange[];
};
type ResponseWithRanges = Omit<Response, "score"> & { score: ScoreWithRanges };

// reducer
export default async function handleWriteShift(
  message: WriteShiftRequest,
  accessToken: string
): Promise<ResponseWithRanges> {
  const { text, docId, language } = message;

  // TODO: language is tricky. It's set globally, but the content script sends it along
  // anyways... this shouldn't be necessary but it feels wrong to omit using part of a payload.
  grammarClient.setLanguage(language);
  try {
    const payload = await grammarClient.postGrammarScore(
      { textData: text, docId },
      accessToken
    );
    if (payload.failCode) {
      return Promise.reject(payload);
    }

    // If there is a score with feedback, get all the HTML ranges for rich content
    if (payload.score?.feedbackList) {
      const feedbackList = payload.score?.feedbackList;
      const ranges = await htmlRangeClient.batchTranslateRanges(
        text,
        feedbackList.map((f) => ({
          offset: f.highlightOffset,
          length: f.highlightLength,
        }))
      );
      const feedbackListWithHtmlRange = feedbackList.map((f, i) => {
        return {
          ...f,
          highlightRange: ranges[i],
        };
      });
      return {
        ...payload,
        score: {
          ...payload.score,
          // Type safety: typescript doesn't change the types after a filter that removes the possibility of null,
          // but we know that safe... right?
          feedbackList: feedbackListWithHtmlRange.filter(
            (f) => f
          ) as FeedbackWithRange[],
        },
      };
    }
    return Promise.reject(payload);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

const WRITESHIFT_CLIENT_FEEDBACK = "WRITESHIFT/CLIENT_FEEDBACK";

export function supportsClientFeedbackMessage(
  message: ExtensionMessage<unknown>
): boolean {
  return message.type === WRITESHIFT_CLIENT_FEEDBACK;
}

export async function handleClientFeedbackMessage(
  message: ExtensionMessage<ClientFeedbackRequest>,
  accessToken: string
): Promise<boolean> {
  const request = message.payload;
  try {
    const response = await grammarClient.postClientFeedback(
      request,
      accessToken
    );
    if (!response.ok) {
      console.warn("Response is not ok", response);
    }
    return response.ok;
  } catch (error) {
    console.error(error);
    return false;
  }
}

// For clients
export function writeshiftCheck(
  text: string,
  language: AdviceLanguage,
  docId?: string
): Promise<ResponseWithRanges> {
  // return browser.runtime.sendMessage({
  //   type: WRITESHIFT_CHECK,
  //   payload: { text, docId, language },
  // });
  return new Promise(() => {
    false;
  });
}

function sendClientFeedback(request: ClientFeedbackRequest): Promise<boolean> {
  // return browser.runtime.sendMessage({
  //   type: WRITESHIFT_CLIENT_FEEDBACK,
  //   payload: request,
  // });
  return new Promise(() => {
    false;
  });
}

export function sendIgnoreClientFeedback(
  request: Omit<ClientFeedbackRequest, "ignored" | "replacementSelection">
): Promise<boolean> {
  return sendClientFeedback({
    ...request,
    ignored: true,
    replacementSelection: null,
  });
}

export function sendSuggestionTakenClientFeedback(
  request: Omit<ClientFeedbackRequest, "ignored">
): Promise<boolean> {
  return sendClientFeedback({
    ...request,
    ignored: false,
  });
}
